/**
 * Avatar Utilities - Google-style default avatar generation
 * 
 * Generates consistent colored avatars with initials when users don't have profile images.
 * Uses a hash-based color selection to ensure the same user always gets the same color.
 */

// Google-inspired color palette (Material Design colors)
const AVATAR_COLORS = [
  '#F44336', // Red
  '#E91E63', // Pink
  '#9C27B0', // Purple
  '#673AB7', // Deep Purple
  '#3F51B5', // Indigo
  '#2196F3', // Blue
  '#03A9F4', // Light Blue
  '#00BCD4', // Cyan
  '#009688', // Teal
  '#4CAF50', // Green
  '#8BC34A', // Light Green
  '#FF9800', // Orange
  '#FF5722', // Deep Orange
  '#795548', // Brown
  '#607D8B', // Blue Grey
];

/**
 * Generate a consistent hash from a string
 * @param {string} str - Input string (username, email, or id)
 * @returns {number} Hash value
 */
const hashString = (str) => {
  if (!str) return 0;
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
};

/**
 * Get a consistent color for a user based on their identifier
 * @param {string|number} identifier - Username, email, or user ID
 * @returns {string} Hex color code
 */
export const getAvatarColor = (identifier) => {
  const hash = hashString(String(identifier || 'default'));
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

/**
 * Extract initials from a username or name
 * @param {string} name - Username or display name
 * @param {number} maxLength - Maximum number of initials (default: 2)
 * @returns {string} Uppercase initials
 */
export const getInitials = (name, maxLength = 2) => {
  if (!name || typeof name !== 'string') return 'U';
  
  // Clean the name and split by spaces, underscores, dots, or camelCase
  const cleaned = name.trim();
  if (!cleaned) return 'U';
  
  // Try to split by common separators
  let parts = cleaned.split(/[\s._-]+/).filter(Boolean);
  
  // If only one part, check for camelCase
  if (parts.length === 1) {
    const camelParts = cleaned.match(/[A-Z][a-z]+|[a-z]+|[A-Z]+(?=[A-Z][a-z]|$)/g);
    if (camelParts && camelParts.length > 1) {
      parts = camelParts;
    }
  }
  
  // Get first letter of each part
  if (parts.length >= 2) {
    return parts
      .slice(0, maxLength)
      .map(part => part[0])
      .join('')
      .toUpperCase();
  }
  
  // Single word - take first two letters or just first letter
  return cleaned.substring(0, maxLength).toUpperCase();
};

/**
 * Generate an SVG data URL for a default avatar
 * @param {string} name - Username or display name
 * @param {string|number} identifier - Unique identifier for consistent coloring (optional, defaults to name)
 * @param {number} size - SVG size in pixels (default: 100)
 * @returns {string} Data URL for the SVG
 */
export const generateAvatarSvg = (name, identifier = null, size = 100) => {
  const initials = getInitials(name);
  const backgroundColor = getAvatarColor(identifier || name);
  const fontSize = size * 0.4; // 40% of size for good proportions
  
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
      <rect width="${size}" height="${size}" fill="${backgroundColor}" rx="${size / 2}" ry="${size / 2}"/>
      <text x="50%" y="50%" dy=".1em" fill="white" font-family="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif" font-size="${fontSize}" font-weight="500" text-anchor="middle" dominant-baseline="middle">${initials}</text>
    </svg>
  `.trim().replace(/\s+/g, ' ');
  
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

/**
 * Check if a profile image URL is valid (not null, undefined, empty, or default)
 * @param {string} imageUrl - Profile image URL
 * @returns {boolean} True if the image URL is valid and usable
 */
export const hasValidProfileImage = (imageUrl) => {
  if (!imageUrl) return false;
  if (typeof imageUrl !== 'string') return false;
  
  const trimmed = imageUrl.trim();
  if (!trimmed) return false;
  
  // Check for default/placeholder images that should trigger the avatar
  const defaultPatterns = [
    'default_profile',
    'placeholder',
    'no-avatar',
    'no_avatar',
  ];
  
  const lowerUrl = trimmed.toLowerCase();
  return !defaultPatterns.some(pattern => lowerUrl.includes(pattern));
};

/**
 * Get avatar display data - returns either the image URL or generated avatar data
 * @param {Object} options - Options object
 * @param {string} options.imageUrl - Profile image URL (can be null/undefined)
 * @param {string} options.name - Username or display name
 * @param {string|number} options.identifier - Unique identifier for consistent coloring
 * @param {boolean} options.isPrivate - Whether the user is private (always show default for privacy)
 * @returns {Object} { type: 'image'|'avatar', src: string, initials: string, color: string }
 */
export const getAvatarDisplayData = ({ imageUrl, name, identifier, isPrivate = false }) => {
  const initials = getInitials(name);
  const color = getAvatarColor(identifier || name);
  
  // Always use generated avatar for private users or when no valid image
  if (isPrivate || !hasValidProfileImage(imageUrl)) {
    return {
      type: 'avatar',
      src: generateAvatarSvg(name, identifier),
      initials,
      color,
    };
  }
  
  return {
    type: 'image',
    src: imageUrl,
    initials,
    color,
  };
};

export default {
  getAvatarColor,
  getInitials,
  generateAvatarSvg,
  hasValidProfileImage,
  getAvatarDisplayData,
};
